import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileSignature, Plus, Send, Download, Trash2, Copy, ArrowLeft, Settings, RefreshCw, Truck } from 'lucide-react';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  driver_employment: 'Driver Employment',
  subcontractor: 'Subcontractor / Hire',
  customer_service: 'Customer Service Agreement',
  trailer_rental: 'Trailer / Crane Rental',
};
const TYPE_COLORS = {
  driver_employment: 'bg-blue-100 text-blue-800',
  subcontractor: 'bg-amber-100 text-amber-800',
  customer_service: 'bg-emerald-100 text-emerald-800',
  trailer_rental: 'bg-purple-100 text-purple-800',
};
const STATUS_COLORS = {
  draft: 'bg-gray-200 text-gray-700',
  sent: 'bg-yellow-100 text-yellow-800',
  signed: 'bg-green-100 text-green-800',
};

const EMPTY_FORM = { contract_type: 'driver_employment', party_name: '', party_email: '', party_phone: '', party_identifier: '', content: '', expiry_date: '' };

const TRAILER_FIELDS = [
  ['agreement_date', 'Agreement Date', 'date'],
  ['commencement_date', 'Commencement Date', 'date'],
  ['end_date', 'End / Expiry Date', 'date'],
  ['lessee_address', 'Lessee Registered Address', 'text'],
  ['lessee_rep', 'Lessee Authorised Rep', 'text'],
  ['trailer_reg', 'Trailer Registration No.', 'text'],
  ['trailer_type', 'Trailer Type', 'text'],
  ['make_model', 'Make / Model', 'text'],
  ['chassis_no', 'Chassis / Serial No.', 'text'],
  ['load_limits', 'Permitted Load / Limits', 'text'],
  ['handover_location', 'Handover Location', 'text'],
  ['operating_area', 'Permitted Operating Area', 'text'],
  ['accessories', 'Accessories Supplied', 'text'],
  ['rental_rate', 'Rental Rate (S$)', 'text'],
  ['rental_basis', 'Rental Basis (Daily/Weekly/Monthly)', 'text'],
  ['operating_hours', 'Operating Hours (e.g. 8am–6pm)', 'text'],
  ['collection_return', 'Collection / Return Arrangement', 'text'],
  ['overtime_rate', 'Overtime Rate (S$/hr)', 'text'],
  ['min_overtime_hours', 'Min Overtime Charge (hrs)', 'text'],
  ['annual_rental', 'Annual Rental (S$)', 'text'],
  ['gst_amount', 'Applicable GST (S$)', 'text'],
  ['total_value', 'Total Contract Value (S$)', 'text'],
  ['upfront_payment', '50% Upfront Payment (S$)', 'text'],
  ['remaining_balance', 'Remaining 50% Balance (S$)', 'text'],
  ['payment_frequency', 'Payment Frequency', 'text'],
  ['instalment_amount', 'Instalment Amount (S$)', 'text'],
  ['payment_due', 'Payment Due Date', 'text'],
  ['final_due', 'Final Payment Due Date', 'text'],
  ['security_deposit', 'Security Deposit (S$)', 'text'],
  ['late_return_charge', 'Late-Return Charge (S$)', 'text'],
  ['insurance_responsibility', 'Insurance Responsibility', 'text'],
  ['gst_treatment', 'GST Treatment', 'text'],
];

const expiryBadge = (c) => {
  if (!c.expiry_date) return null;
  const days = Math.ceil((new Date(c.expiry_date) - new Date()) / 86400000);
  if (days < 0) return <Badge className="bg-red-100 text-red-800">Expired {Math.abs(days)}d ago</Badge>;
  if (days <= 7) return <Badge className="bg-red-100 text-red-800">Expires in {days}d</Badge>;
  if (days <= 30) return <Badge className="bg-amber-100 text-amber-800">Expires in {days}d</Badge>;
  return <Badge className="bg-gray-100 text-gray-600">Expires {c.expiry_date}</Badge>;
};

export default function Contracts() {
  const [contracts, setContracts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [tplType, setTplType] = useState('driver_employment');
  const [tplContent, setTplContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [renewTarget, setRenewTarget] = useState(null);
  const [renewExpiry, setRenewExpiry] = useState('');
  const [fillValues, setFillValues] = useState({});
  const [trailers, setTrailers] = useState([]);
  const [showFleet, setShowFleet] = useState(false);
  const [trailerForm, setTrailerForm] = useState({ registration_no: '', trailer_type: '', make_model: '', chassis_no: '', load_limits: '' });

  const load = async () => {
    try {
      const [cRes, tRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/contracts/`),
        axios.get(`${BACKEND_URL}/api/contracts/templates`),
      ]);
      setContracts(cRes.data.contracts || []);
      setTemplates(tRes.data.templates || []);
      axios.get(`${BACKEND_URL}/api/trailers/availability`).then((r) => setTrailers(r.data.trailers || [])).catch(() => {});
    } catch (e) {
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const tplFor = (type) => templates.find((t) => t.contract_type === type);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, content: tplFor('driver_employment')?.content || '' });
    setFillValues({});
    setShowCreate(true);
  };

  const onTypeChange = (type) => {
    setForm({ ...form, contract_type: type, content: tplFor(type)?.content || '' });
  };

  const handleCreate = async () => {
    if (!form.party_name.trim()) { toast.error('Party name is required'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/contracts/`, {
        ...form,
        expiry_date: form.expiry_date || fillValues.end_date || '',
        fill_values: fillValues,
      });
      toast.success(`Contract ${res.data.document_number} created`);
      setShowCreate(false);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to create contract'));
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async (c) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/contracts/${c.id}`);
      await navigator.clipboard.writeText(res.data.sign_url);
      toast.success('Signing link copied');
    } catch (e) {
      toast.error('Failed to copy link');
    }
  };

  const sendContract = async (c) => {
    try {
      await axios.post(`${BACKEND_URL}/api/contracts/${c.id}/send`);
      toast.success(`Signing link emailed to ${c.party_email}`);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to send'));
    }
  };

  const deleteContract = async (c) => {
    if (!window.confirm(`Delete contract ${c.document_number}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/contracts/${c.id}`);
      toast.success('Contract deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleRenew = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/contracts/${renewTarget.id}/renew`, { expiry_date: renewExpiry });
      toast.success(`Renewed as ${res.data.document_number} — fresh signing link ready`);
      setRenewTarget(null);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to renew contract'));
    } finally {
      setSaving(false);
    }
  };

  const pickTrailer = (trailerId) => {
    const t = trailers.find((x) => x.id === trailerId);
    if (!t) return;
    setFillValues({
      ...fillValues,
      trailer_reg: t.registration_no || '',
      trailer_type: t.trailer_type || '',
      make_model: t.make_model || '',
      chassis_no: t.chassis_no || '',
      load_limits: t.load_limits || '',
    });
    if (t.availability === 'on_rental') {
      toast.warning(`${t.registration_no} is already on rental to ${t.rented_to} (${t.contract_number})${t.rental_until ? ` until ${t.rental_until}` : ''} — double-check before sending!`);
    } else if (t.availability === 'reserved') {
      toast.warning(`${t.registration_no} is reserved under ${t.contract_number} (awaiting signature) — double-check before sending!`);
    } else {
      toast.success(`Trailer ${t.registration_no} details filled — available`);
    }
  };

  const addTrailer = async () => {
    if (!trailerForm.registration_no.trim()) { toast.error('Registration number is required'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/trailers/`, trailerForm);
      toast.success(`Trailer ${trailerForm.registration_no.toUpperCase()} added to fleet`);
      setTrailerForm({ registration_no: '', trailer_type: '', make_model: '', chassis_no: '', load_limits: '' });
      const r = await axios.get(`${BACKEND_URL}/api/trailers/availability`);
      setTrailers(r.data.trailers || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to add trailer'));
    }
  };

  const deleteTrailer = async (t) => {
    if (!window.confirm(`Remove trailer ${t.registration_no} from the fleet?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/trailers/${t.id}`);
      setTrailers(trailers.filter((x) => x.id !== t.id));
      toast.success('Trailer removed');
    } catch (e) {
      toast.error('Failed to remove trailer');
    }
  };

  const openTemplates = () => {
    setTplType('driver_employment');
    setTplContent(tplFor('driver_employment')?.content || '');
    setShowTemplates(true);
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/contracts/templates/${tplType}`, { content: tplContent });
      toast.success('Template saved — future contracts will use this wording');
      await load();
    } catch (e) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12" data-testid="contracts-page">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <Link to="/admin/billing" className="text-sm text-gray-500 flex items-center gap-1 mb-1"><ArrowLeft size={14} /> Billing</Link>
            <h1 className="text-2xl font-bold flex items-center gap-2"><FileSignature className="text-[#5c1a1a]" /> Contracts</h1>
            <p className="text-sm text-gray-500">Driver employment, subcontractor and customer agreements — signed online</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowFleet(true)} data-testid="trailer-fleet-btn"><Truck size={16} className="mr-1" /> Trailer Fleet</Button>
            <Button variant="outline" onClick={openTemplates} data-testid="edit-templates-btn"><Settings size={16} className="mr-1" /> Edit Templates</Button>
            <Button className="bg-[#5c1a1a] hover:bg-[#4a1515] text-white" onClick={openCreate} data-testid="new-contract-btn"><Plus size={16} className="mr-1" /> New Contract</Button>
          </div>
        </div>

        {loading ? (
          <p className="text-gray-400">Loading…</p>
        ) : contracts.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-gray-400">No contracts yet. Create one and send the signing link.</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {contracts.map((c) => (
              <Card key={c.id} data-testid={`contract-row-${c.document_number}`}>
                <CardContent className="py-4 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold">{c.document_number}</span>
                      <Badge className={TYPE_COLORS[c.contract_type]}>{TYPE_LABELS[c.contract_type]}</Badge>
                      <Badge className={STATUS_COLORS[c.status] || ''}>{c.status}</Badge>
                      {expiryBadge(c)}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{c.party_name} {c.party_email && `· ${c.party_email}`}</p>
                    {c.signed_at && <p className="text-xs text-green-700">Signed by {c.signer_name} on {new Date(c.signed_at).toLocaleString('en-SG')}</p>}
                    {c.renewed_to && <p className="text-xs text-blue-700">Renewed → {c.renewed_to}</p>}
                    {c.renewed_from && <p className="text-xs text-gray-400">Renewal of {c.renewed_from}</p>}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => copyLink(c)} data-testid={`copy-link-${c.document_number}`}><Copy size={14} className="mr-1" /> Link</Button>
                    {c.status !== 'signed' && c.party_email && (
                      <Button size="sm" variant="outline" onClick={() => sendContract(c)} data-testid={`send-contract-${c.document_number}`}><Send size={14} className="mr-1" /> Email</Button>
                    )}
                    <a href={`${BACKEND_URL}/api/contracts/${c.id}/pdf`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline" data-testid={`pdf-contract-${c.document_number}`}><Download size={14} className="mr-1" /> PDF</Button>
                    </a>
                    {!c.renewed_to && (
                      <Button size="sm" variant="outline" className="text-blue-700" onClick={() => { setRenewTarget(c); setRenewExpiry(''); }} data-testid={`renew-contract-${c.document_number}`}><RefreshCw size={14} className="mr-1" /> Renew</Button>
                    )}
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteContract(c)} data-testid={`delete-contract-${c.document_number}`}><Trash2 size={14} /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Contract</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Contract Type</Label>
              <Select value={form.contract_type} onValueChange={onTypeChange}>
                <SelectTrigger data-testid="contract-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`type-option-${k}`}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Party Name *</Label><Input value={form.party_name} onChange={(e) => setForm({ ...form, party_name: e.target.value })} data-testid="contract-party-name" /></div>
              <div><Label>NRIC / UEN</Label><Input value={form.party_identifier} onChange={(e) => setForm({ ...form, party_identifier: e.target.value })} data-testid="contract-party-id" /></div>
              <div><Label>Email</Label><Input type="email" value={form.party_email} onChange={(e) => setForm({ ...form, party_email: e.target.value })} data-testid="contract-party-email" /></div>
              <div><Label>Phone</Label><Input value={form.party_phone} onChange={(e) => setForm({ ...form, party_phone: e.target.value })} data-testid="contract-party-phone" /></div>
              <div><Label>Expiry / Renewal Date <span className="text-gray-400 font-normal">(optional — you'll get email reminders)</span></Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="contract-expiry-date" /></div>
            </div>
            {form.contract_type === 'trailer_rental' && (
              <div className="border border-purple-200 bg-purple-50/40 rounded-lg p-4">
                <p className="text-sm font-semibold text-purple-900 mb-1">Fill-In Details</p>
                <p className="text-xs text-gray-500 mb-3">These values replace the blanks in the agreement automatically. Anything left empty stays as a blank line.</p>
                {trailers.length > 0 && (
                  <div className="mb-4">
                    <Label className="text-xs text-gray-500">Pick from Trailer Fleet</Label>
                    <Select onValueChange={pickTrailer}>
                      <SelectTrigger data-testid="trailer-picker" className="h-9 bg-white"><SelectValue placeholder="Select a trailer to auto-fill reg / type / chassis…" /></SelectTrigger>
                      <SelectContent>
                        {trailers.map((t) => (
                          <SelectItem key={t.id} value={t.id} data-testid={`trailer-option-${t.registration_no}`}>
                            {t.registration_no}{t.trailer_type ? ` — ${t.trailer_type}` : ''}{t.make_model ? ` (${t.make_model})` : ''}
                            {t.availability === 'on_rental' ? ' • ⛔ ON RENTAL' : t.availability === 'reserved' ? ' • ⏳ RESERVED' : ' • ✓ Available'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {TRAILER_FIELDS.map(([key, label, type]) => (
                    <div key={key}>
                      <Label className="text-xs text-gray-500">{label}</Label>
                      <Input type={type} className="h-9" value={fillValues[key] || ''} onChange={(e) => setFillValues({ ...fillValues, [key]: e.target.value })} data-testid={`fill-${key}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label>Contract Wording <span className="text-gray-400 font-normal">(prefilled from template — edit placeholders like [Start Date] before sending)</span></Label>
              <Textarea rows={14} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="font-mono text-xs" data-testid="contract-content" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-[#5c1a1a] hover:bg-[#4a1515] text-white" onClick={handleCreate} disabled={saving} data-testid="create-contract-btn">{saving ? 'Creating…' : 'Create Contract'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Renew dialog */}
      <Dialog open={!!renewTarget} onOpenChange={(o) => !o && setRenewTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Renew {renewTarget?.document_number}</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">Creates a fresh copy of this contract for <strong>{renewTarget?.party_name}</strong> with a new signing link. The wording and party details are carried over.</p>
          <div>
            <Label>New Expiry / Renewal Date <span className="text-gray-400 font-normal">(optional)</span></Label>
            <Input type="date" value={renewExpiry} onChange={(e) => setRenewExpiry(e.target.value)} data-testid="renew-expiry-date" className="mt-1" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewTarget(null)}>Cancel</Button>
            <Button className="bg-[#5c1a1a] hover:bg-[#4a1515] text-white" onClick={handleRenew} disabled={saving} data-testid="confirm-renew-btn">{saving ? 'Renewing…' : 'Renew Contract'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trailer Fleet dialog */}
      <Dialog open={showFleet} onOpenChange={setShowFleet}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Truck size={18} /> Trailer Fleet</DialogTitle></DialogHeader>
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/60">
            <p className="text-sm font-semibold mb-3">Add Trailer</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div><Label className="text-xs text-gray-500">Registration No. *</Label><Input className="h-9" value={trailerForm.registration_no} onChange={(e) => setTrailerForm({ ...trailerForm, registration_no: e.target.value })} data-testid="fleet-reg-input" /></div>
              <div><Label className="text-xs text-gray-500">Trailer Type</Label><Input className="h-9" value={trailerForm.trailer_type} onChange={(e) => setTrailerForm({ ...trailerForm, trailer_type: e.target.value })} data-testid="fleet-type-input" /></div>
              <div><Label className="text-xs text-gray-500">Make / Model</Label><Input className="h-9" value={trailerForm.make_model} onChange={(e) => setTrailerForm({ ...trailerForm, make_model: e.target.value })} data-testid="fleet-make-input" /></div>
              <div><Label className="text-xs text-gray-500">Chassis / Serial No.</Label><Input className="h-9" value={trailerForm.chassis_no} onChange={(e) => setTrailerForm({ ...trailerForm, chassis_no: e.target.value })} data-testid="fleet-chassis-input" /></div>
              <div><Label className="text-xs text-gray-500">Permitted Load / Limits</Label><Input className="h-9" value={trailerForm.load_limits} onChange={(e) => setTrailerForm({ ...trailerForm, load_limits: e.target.value })} data-testid="fleet-load-input" /></div>
              <div className="flex items-end"><Button className="bg-[#5c1a1a] hover:bg-[#4a1515] text-white h-9 w-full" onClick={addTrailer} data-testid="fleet-add-btn"><Plus size={14} className="mr-1" /> Add</Button></div>
            </div>
          </div>
          <div className="space-y-2 mt-2">
            {trailers.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">No trailers yet — add your fleet above.</p>
            ) : trailers.map((t) => (
              <div key={t.id} className="flex items-center justify-between border rounded-lg px-4 py-2.5" data-testid={`fleet-row-${t.registration_no}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{t.registration_no}</span>
                    {t.availability === 'on_rental' ? (
                      <Badge className="bg-red-100 text-red-800" data-testid={`availability-${t.registration_no}`}>On Rental{t.rental_until ? ` until ${t.rental_until}` : ''}</Badge>
                    ) : t.availability === 'reserved' ? (
                      <Badge className="bg-amber-100 text-amber-800" data-testid={`availability-${t.registration_no}`}>Reserved — {t.contract_number}</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-800" data-testid={`availability-${t.registration_no}`}>Available</Badge>
                    )}
                  </div>
                  <span className="text-sm text-gray-500">{[t.trailer_type, t.make_model, t.chassis_no && `Chassis: ${t.chassis_no}`, t.load_limits].filter(Boolean).join(' · ')}</span>
                  {t.availability === 'on_rental' && <p className="text-xs text-red-600">Rented to {t.rented_to} ({t.contract_number})</p>}
                </div>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteTrailer(t)} data-testid={`fleet-delete-${t.registration_no}`}><Trash2 size={14} /></Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Templates dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Contract Templates</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Select value={tplType} onValueChange={(t) => { setTplType(t); setTplContent(tplFor(t)?.content || ''); }}>
              <SelectTrigger data-testid="template-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea rows={18} value={tplContent} onChange={(e) => setTplContent(e.target.value)} className="font-mono text-xs" data-testid="template-content" />
            <p className="text-xs text-gray-400">Paste your final contract wording here anytime — lines starting with "1." "2." etc. become bold section headers in the PDF.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplates(false)}>Close</Button>
            <Button className="bg-[#5c1a1a] hover:bg-[#4a1515] text-white" onClick={saveTemplate} disabled={saving} data-testid="save-template-btn">{saving ? 'Saving…' : 'Save Template'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
